import React, { useRef, useEffect } from 'react';
import { Text } from 'drei';
import { useGesture } from 'react-use-gesture';
import { useSpring, a, config } from 'react-spring/three';
import { Vector3 } from 'three';
import { useMedia } from '../../../hooks';
import { numberToShow } from '../../../common/utils';
import { MediaEvents } from '../../../common/media';
import { useThree } from 'react-three-fiber';

type TimelineProps = {
  target: string;
};

const Timeline = ({ target }: TimelineProps) => {
  const { gl } = useThree();

  const [mainSpring, setMain] = useSpring(() => ({
    scale: [1, 1, 1],
    config: config.wobbly,
  }));

  const lineRef = useRef<any>(null);
  const textRef = useRef<any>(null);
  const media = useMedia(target);
  const wrapperWidth = 0.25;

  const bind = useGesture({
    onHover: ({ hovering }) => {
      if (hovering) {
        setTimeout(() => {
          gl.domElement.classList.add('cursor-pointer');
        });
      } else {
        gl.domElement.classList.remove('cursor-pointer');
      }
      setMain({ scale: hovering ? [1, 1.5, 1] : [1, 1, 1] });
    },
    onClick: ({ event }) => {
      const evt = event as any;
      evt.nativeEvent.preventDefault();
      evt.nativeEvent.stopPropagation();
event.stopPropagation();

      const wrapper = (event as any).object;

      const wrapperX = Math.abs(wrapper.position.x);

      const worldClickCoord = (event as any).point;
      const clickCoord = wrapper.parent.worldToLocal(
        new Vector3(worldClickCoord.x, worldClickCoord.y, worldClickCoord.z)
      );

      const coordByPr = (clickCoord.x + wrapperX + wrapperWidth / 2) / wrapperWidth; // 0 - 1 range

      const totalTime = media?.getDuration() || 0;

      let time = coordByPr * totalTime;

      if (time > totalTime) {
        time = totalTime;
      }

      media?.setCurrentTime(time);
    },
  });

  useEffect(() => {
    if (media) {
      media.on(MediaEvents.Timeupdate, ({ currentTime, duration }) => {
        if (textRef.current && lineRef.current) {
          const totalMin = Math.floor(duration / 60) || 0;
          const totalSec = Math.floor(duration - totalMin * 60) || 0;
          const currentMin = Math.floor(currentTime / 60) || 0;
          const currentSec = Math.floor(currentTime - currentMin * 60) || 0;

          let scaleX = currentTime / duration;
          let x = -(wrapperWidth / 2 - (scaleX * wrapperWidth) / 2);

          if (isNaN(scaleX)) {
            scaleX = 0;
            x = 0;
          }

          const text = `${numberToShow(currentMin)}:${numberToShow(currentSec)} | ${numberToShow(
            totalMin
          )}:${numberToShow(totalSec)}`;

          textRef.current.text = text;
          lineRef.current.scale.set(scaleX, 1, 1);
          lineRef.current.position.x = x;
        }
      });
    }
  }, [media]);

  return (
    <group position={[-0.06, 0, 0]}>
      <a.mesh {...mainSpring} {...bind()}>
        <planeBufferGeometry args={[0.25, 0.005]} attach="geometry" />
        <meshBasicMaterial attach="material" color="#686868" />

        <mesh ref={lineRef} position={[0, 0, 0.001]} scale={[0, 1, 1]}>
          <planeBufferGeometry args={[0.25, 0.006]} attach="geometry" />
          <meshBasicMaterial attach="material" color="#fff" />
        </mesh>
      </a.mesh>
      <group position={[0.19, 0, 0]}>
        <Text
          ref={textRef}
          color="#fff"
          fontSize={0.016}
          maxWidth={0.098}
          lineHeight={1}
          textAlign="center"
          anchorX="center"
          anchorY="middle">
          00:00 | 00:00
        </Text>
      </group>
    </group>
  );
};

export default Timeline;
